// =============================================================================
// VPC FLOW LOG TYPES
// =============================================================================

export interface VPCFlowRecord {
  timestamp: string;
  eniId: string;
  version: number;
  accountId: string;
  srcAddr: string;
  dstAddr: string;
  srcPort: number;
  dstPort: number;
  protocol: number; // 6=TCP, 17=UDP, 1=ICMP
  packets: number;
  bytes: number;
  startTime: number;
  endTime: number;
  action: 'ACCEPT' | 'REJECT';
  logStatus: 'OK' | 'NODATA' | 'SKIPDATA';
}

export interface FlowStream {
  id: string;
  eniId: string;
  srcAddr: string;
  dstAddr: string;
  srcPort: number;
  dstPort: number;
  protocol: number;
  protocolName: string;
  direction: 'inbound' | 'outbound' | 'unknown';
  action: 'ACCEPT' | 'REJECT';
  totalPackets: number;
  totalBytes: number;
  flowCount: number;
  firstSeen: string;
  lastSeen: string;
  records?: VPCFlowRecord[]; // Optional - not included when parsed by Web Worker
  
  // Stateful connection detection
  connectionType?: 'initiator' | 'response' | 'unknown';
  relatedStreamId?: string; // ID of the reverse flow (if detected)
  serverPort?: number; // The well-known port (e.g., 443, 80, 22)
  isStatefulAllowed?: boolean; // True if allowed due to stateful SG behavior
}

// Well-known ports that indicate the "server" side
export const WELL_KNOWN_PORTS = new Set([
  20, 21, 22, 23, 25, 53, 80, 110, 143, 443, 465, 587, 993, 995, 
  1433, 1521, 3306, 3389, 5432, 5900, 6379, 8080, 8443, 9200, 27017
]);

// =============================================================================
// TRAFFIC PATH ANALYSIS TYPES
// =============================================================================

export type HopType =
  | 'ENI'
  | 'SUBNET'
  | 'NACL'
  | 'SG'
  | 'ROUTE_TABLE'
  | 'NAT_GW'
  | 'IGW'
  | 'TGW'
  | 'VPC_PEERING'
  | 'VPC'
  | 'EXTERNAL'
  | 'VGW'
  | 'VPCE';

export interface MatchedRule {
  ruleNumber?: number;
  action: 'allow' | 'deny';
  protocol: string;
  portRange: string;
  cidr: string;
}

export interface MatchedRoute {
  destination: string;
  target: string;
  targetType: string;
}

export interface TrafficHop {
  type: HopType;
  id: string;
  name: string;
  details: {
    matchedRule?: MatchedRule;
    matchedRoute?: MatchedRoute;
    port?: number;
    ip?: string;
    cidr?: string;
    publicIp?: string;
    privateIp?: string;
  };
  status: 'allowed' | 'denied' | 'unknown';
}

export interface TrafficPath {
  stream: FlowStream;
  inboundPath: TrafficHop[];
  outboundPath: TrafficHop[];
  verdict: 'allowed' | 'denied';
  deniedAt?: TrafficHop;
}

// =============================================================================
// DISCOVERY DATA INDEX TYPES
// =============================================================================

export interface NetworkInterface {
  id: string;
  subnet_id: string;
  vpc_id?: string;
  private_ip_address: string;
  private_ip_addresses?: Array<{ PrivateIpAddress: string }>;
  security_groups?: string[];
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface Subnet {
  id: string;
  cidr_block: string;
  vpc_id?: string;
  availability_zone?: string;
  tags?: { Name?: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface Route {
  DestinationCidrBlock?: string;
  GatewayId?: string;
  NatGatewayId?: string;
  TransitGatewayId?: string;
  VpcPeeringConnectionId?: string;
  NetworkInterfaceId?: string;
  State?: string;
  Origin?: string;
}

export interface RouteTable {
  id: string;
  vpc_id?: string;
  routes: Route[];
  associations: Array<{
    SubnetId?: string;
    Main?: boolean;
    RouteTableAssociationId?: string;
  }>;
  tags?: { Name?: string };
}

export interface NACLEntry {
  RuleNumber: number;
  RuleAction: 'allow' | 'deny';
  Protocol: string;
  CidrBlock: string;
  Egress: boolean;
  PortRange?: { From: number; To: number };
}

export interface NetworkAcl {
  id: string;
  vpc_id?: string;
  entries: NACLEntry[];
  associations: Array<{ SubnetId: string }>;
  tags?: { Name?: string };
}

export interface SecurityGroupRule {
  IpProtocol: string;
  FromPort?: number;
  ToPort?: number;
  IpRanges?: Array<{ CidrIp: string; Description?: string }>;
  UserIdGroupPairs?: Array<{ GroupId: string }>;
}

export interface SecurityGroup {
  id: string;
  group_name?: string;
  description?: string;
  vpc_id?: string;
  ip_permissions: SecurityGroupRule[];
  ip_permissions_egress: SecurityGroupRule[];
  tags?: { Name?: string };
}

export interface VPC {
  id?: string;
  cidr_block: string;
  tags?: { Name?: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface InternetGateway {
  id: string;
  tags?: { Name?: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface NatGateway {
  id: string;
  subnet_id: string;
  nat_gateway_addresses?: Array<{
    PublicIp?: string;
    PrivateIp?: string;
    NetworkInterfaceId?: string;
  }>;
  tags?: { Name?: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface TransitGatewayAttachment {
  id: string;
  transit_gateway_id?: string;
  vpc_id?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface VPCPeeringConnection {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface DiscoveryIndex {
  eniMap: Map<string, NetworkInterface>;
  ipToEni: Map<string, string>;
  subnetMap: Map<string, Subnet>;
  cidrToSubnet: Array<{ cidr: string; subnetId: string; vpcId: string }>;
  routeTableMap: Map<string, RouteTable>;
  subnetToRouteTable: Map<string, string>;
  naclMap: Map<string, NetworkAcl>;
  subnetToNacl: Map<string, string>;
  securityGroupMap: Map<string, SecurityGroup>;
  vpcMap: Map<string, VPC>;
  vpcCidrs: string[];
  igwMap: Map<string, InternetGateway>;
  natGwMap: Map<string, NatGateway>;
  tgwAttachmentMap: Map<string, TransitGatewayAttachment>;
  vpcPeeringMap: Map<string, VPCPeeringConnection>;
}

// =============================================================================
// UI STATE TYPES
// =============================================================================

export type TrafficAnalyzerView = 'upload' | 'list' | 'visualization';

export interface StreamFilter {
  protocol: 'all' | 'tcp' | 'udp' | 'icmp';
  action: 'all' | 'ACCEPT' | 'REJECT';
  direction: 'all' | 'inbound' | 'outbound';
  searchIp: string;
}

export interface TrafficAnalyzerState {
  view: TrafficAnalyzerView;
  flowLogs: VPCFlowRecord[];
  streams: FlowStream[];
  selectedStreams: Set<string>;
  analysisResults: TrafficPath[];
  filter: StreamFilter;
  loading: boolean;
  error: string | null;
}

